import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    MenuItem,
    Select,
    TextField,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningItem, SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { FormField } from "../../../../shared/formField";
import { FormSection } from "../../../../shared/formSection";
import { ValidationErrors } from "../../../../shared/validationErrors";
import { useApi } from "../../../../useApi";

const BIOMETRIC_DEVICE_TYPES = ["Fingerprint", "Palm"];

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    existingItems: Record<string, SiteCommissioningItem>;
    editingSerialNumber?: string;
    onSaved: (summary: SiteCommissioningSummary) => void;
}

export const EditBiometricDeviceDialog: React.FC<Props> = ({ open, onClose, siteId, existingItems, editingSerialNumber, onSaved }) => {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [serialNumber, setSerialNumber] = useState("");
    const [type, setType] = useState(BIOMETRIC_DEVICE_TYPES[0]);
    const [existingPeriodicSyncEnabled, setExistingPeriodicSyncEnabled] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const isEditing = Boolean(editingSerialNumber);

    useEffect(() => {
        if (open) {
            const existingFields = editingSerialNumber ? existingItems[editingSerialNumber]?.fields : undefined;
            setSerialNumber(editingSerialNumber ?? "");
            setType(typeof existingFields?.type === "string" ? existingFields.type : BIOMETRIC_DEVICE_TYPES[0]);
            setExistingPeriodicSyncEnabled(Boolean(existingFields?.periodicSyncEnabled));
            setErrors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingSerialNumber]);

    const save = () => {
        const trimmedSerial = serialNumber.trim().toUpperCase();
        if (!trimmedSerial) {
            setErrors(["Serial number is required."]);
            return;
        }
        if (!isEditing && Object.keys(existingItems).length > 0) {
            setErrors(["Only one biometric device is allowed per site. Edit the existing one instead of adding another."]);
            return;
        }

        const items: Record<string, SiteCommissioningItem> = { ...existingItems };
        if (isEditing && editingSerialNumber !== trimmedSerial) {
            delete items[editingSerialNumber as string];
        }
        items[trimmedSerial] = { fields: { type, periodicSyncEnabled: existingPeriodicSyncEnabled } };

        setSaving(true);
        siteApi
            .updateCommissioning(siteId, [{ name: "biometricDevices", items }])
            .then((r) => {
                setSaving(false);
                if (r.result === AjaxResult.Success && r.data) {
                    onSaved(r.data);
                    onClose();
                    enqueueSnackbar(isEditing ? "Biometric device updated" : "Biometric device added", { variant: "success" });
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to save the biometric device", { variant: "error" });
                }
            });
    };

    return (
        <Dialog open={open} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText={isEditing ? "Edit Biometric Device" : "Add Biometric Device"}>
                    <FormField label="Serial Number" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={serialNumber}
                                onChange={(event) => setSerialNumber(event.currentTarget.value)}
                                variant="outlined"
                                disabled={isEditing}
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField label="Type" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <Select value={type} onChange={(event) => setType(event.target.value as string)} variant="outlined">
                                {BIOMETRIC_DEVICE_TYPES.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormField>
                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" variant="contained" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
